// Shared helpers for the open record driven screens (Manage tab document drill down,
// Activity Log tab and Statistics tab).
//
// "PendingFullScreen" open records are placeholders written when a reader is asked to
// switch to full screen but has not actually started a reading session yet. They are not
// real reads, so they must not show up in any activity log grid/CSV and the page
// statistics rows they generate must not be counted.

export const PENDING_FULL_SCREEN_EVENT = "PendingFullScreen";

// Defensive compare, the API has returned the value with different casing/spacing before.
export const isPendingFullScreenRecord = (record) => {
  const event = record && record.event;
  if (typeof event !== "string") return false;
  return event.replace(/\s+/g, "").toLowerCase() === "pendingfullscreen";
};

// Drops the PendingFullScreen rows from a list of open records.
export const excludePendingFullScreenRecords = (records) =>
  Array.isArray(records)
    ? records.filter((record) => !isPendingFullScreenRecord(record))
    : [];

// Ids of the open records that must never contribute to the statistics rows.
export const getPendingFullScreenRecordIds = (records) => {
  const ids = new Set();
  if (!Array.isArray(records)) return ids;
  records.forEach((record) => {
    if (record && record.id != null && isPendingFullScreenRecord(record)) {
      ids.add(record.id.toString());
    }
  });
  return ids;
};

const toNumber = (value) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

export const getStatisticsRowSeconds = (stat) =>
  stat
    ? toNumber(stat.totalHours) * 3600 +
    toNumber(stat.totalMinutes) * 60 +
    toNumber(stat.totalSeconds)
    : 0;

// A page 1 row with no time spent and a single read per session is the artefact left
// behind by a session that never really started (the PendingFullScreen case), so it is
// not a statistics record.
export const isEmptyFirstPageStatisticsRow = (stat) =>
  !!stat &&
  toNumber(stat.pageNumber) === 1 &&
  getStatisticsRowSeconds(stat) === 0 &&
  toNumber(stat.totalReadsPerSession) === 1;

// Removes the statistics rows that belong to a PendingFullScreen open record, plus the
// empty page 1 rows described above. openRecords is optional, the mapping is only applied
// when the statistics rows carry an openRecordId.
export const filterStatisticsRows = (rows, openRecords) => {
  if (!Array.isArray(rows)) return [];
  const pendingIds = getPendingFullScreenRecordIds(openRecords);
  return rows.filter((stat) => {
    if (!stat) return false;
    if (
      stat.openRecordId != null &&
      pendingIds.has(stat.openRecordId.toString())
    ) {
      return false;
    }
    return !isEmptyFirstPageStatisticsRow(stat);
  });
};
